import React from 'react';
import { Facebook, Instagram } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const FooterLink: React.FC<{ href: string; children: React.ReactNode }> = ({
  href,
  children,
}) => (
  <li>
    <a
      href={href}
      className="text-blue-200 hover:text-white transition-colors duration-300 pb-1 border-b-2 border-transparent hover:border-yellow-400"
    >
      {children}
    </a>
  </li>
);

const SocialLink: React.FC<{
  href: string;
  icon: React.ReactNode;
  label: string;
}> = ({ href, icon, label }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="text-blue-300 hover:text-white transition-colors duration-300"
    aria-label={label}
  >
    {icon}
  </a>
);

const Footer: React.FC = () => {
  const { t, language } = useLanguage();
  
  const socialLinks = [
    {
      href: '#',
      label: 'Facebook',
      icon: <Facebook className="h-6 w-6" />,
    },
    {
      href: '#',
      label: 'Instagram',
      icon: <Instagram className="h-6 w-6" />,
    },
  ];

  return (
    <footer className="bg-gradient-to-br from-blue-900 to-gray-900 text-white">
      <div className="container py-16 px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Logo and Info */}
          <div className="md:col-span-2 lg:col-span-1">
            <img
              src="/images/MR-Logo.png"
              alt="家家顺搬家公司 Logo"
              className="h-12 mb-4 bg-white p-2 rounded-lg"
            />
            <p className="mt-4 text-blue-200 text-base">
              {t('footerDescription')}
            </p>
            <div className="mt-6 flex space-x-5">
              {socialLinks.map((link) => (
                <SocialLink key={link.label} {...link} />
              ))}
            </div>
          </div>

          {/* Services Links */}
          <div>
            <h3 className="text-base font-bold text-white tracking-wider uppercase">
              {t('footerServices')}
            </h3>
            <ul className="mt-4 space-y-4">
              <FooterLink href="#services">{t('footerResidential')}</FooterLink>
              <FooterLink href="#services">{t('footerOffice')}</FooterLink>
              <FooterLink href="#services">{t('footerSpecial')}</FooterLink>
              <FooterLink href="#services">{t('footerPacking')}</FooterLink>
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="text-base font-bold text-white tracking-wider uppercase">
              {t('footerCompany')}
            </h3>
            <ul className="mt-4 space-y-4">
              <FooterLink href="#about">{t('footerAbout')}</FooterLink>
              <FooterLink href="#testimonials">{t('footerTestimonials')}</FooterLink>
              <FooterLink href="#contact">{t('footerContact')}</FooterLink>
              <FooterLink href="#">{t('footerPrivacy')}</FooterLink>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-base font-bold text-white tracking-wider uppercase">
              {t('footerContactInfo')}
            </h3>
            <ul className="mt-4 space-y-4 text-blue-200">
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 mr-3 mt-1 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                <span>0450 688 920</span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 mr-3 mt-1 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <span>michaelremovalists@gmail.com</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 border-t border-white/10 pt-8">
          <div className="flex flex-col items-center space-y-2">
            <p className="text-base text-blue-300 text-center">
              &copy; {new Date().getFullYear()} Michael Removalist (家家顺搬家公司). {t('footerCopyright')}
            </p>
            <p className="text-sm text-blue-400 text-center">
              {language === 'zh' ? (
                <>
                  网站由{' '}
                  <a
                    href="https://flexsolutions.com.au/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-yellow-400 hover:text-yellow-300 transition-colors duration-300 underline"
                  >
                    Flex Solutions
                  </a>
                  {' '}设计开发
                </>
              ) : (
                <>
                  Website powered & designed by{' '}
                  <a
                    href="https://flexsolutions.com.au/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-yellow-400 hover:text-yellow-300 transition-colors duration-300 underline"
                  >
                    Flex Solutions
                  </a>
                </>
              )}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
